<template>
  <v-btn
    color="deep-purple-lighten-1"
    @click="dialog = true"
  >
    {{ $t('event.absences.absences_button') }}
  </v-btn>

  <v-dialog v-model="dialog" max-width="900px">
    <v-card>
      <v-card-text>
        <!-- Tabulka omluveni -->
        <v-data-table
          :headers="excusedHeaders"
          :items="excused"
          :loading=loading
          loading-text="general.loading"
          class="mb-6"
          no-data-text="general.no_data"
          :hide-default-footer="true"
          density="compact"
          striped="even"
          items-per-page="-1"

        >
          <template #top>
            <h3 class="subtitle-1 font-weight-medium">{{ $t('event.absences.excused_title') }}</h3>
          </template>

          <template #item.name="{ item }">
            <v-tooltip location="top center" origin="auto" :text="$formatDateTime(item.timestamp)">
              <template #activator="{ props }">
                <span v-bind="props">
                  {{ item.name }}
                </span>
              </template>
            </v-tooltip>
          </template>

        </v-data-table>

        <!-- Tabulka neomluveni -->
        <v-data-table
          :headers="unexcusedHeaders"
          :items="unexcused"
          :loading=loading
          loading-text="general.loading"
          no-data-text="general.no_data"
          :hide-default-footer="true"
          :hide-default-header="true"
          density="compact"
          striped="even"
          items-per-page="-1"
        >
          <template #top>
            <v-row class="mb-2 align-center" no-gutters>
              <v-col cols="12" sm="7">
                <h3 class="subtitle-1 font-weight-medium text-truncate">
                  {{ $t('event.absences.unexcused_title') }} ({{ (props.eventType || '').toUpperCase() }})
                </h3>
              </v-col>

              <v-col cols="12" sm="5" class="d-flex justify-end">
                <v-btn
                  color="primary"
                  class="w-100 btn-ellipsis"
                  @click="copyUnexcusedEmails"
                >
                  <span class="d-block w-100 text-truncate">
                    {{ $t('event.absences.copy_unexcused_emails') }}
                  </span>
                </v-btn>
              </v-col>
            </v-row>
          </template>

          <template #item.name="{ item }">
            <span class="text-no-wrap">{{ item.name }}</span>
          </template>

        </v-data-table>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="dialog = false">{{ $t('general.close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import apiClient from '/api.js'
import {useSnackbarStore} from '@/stores/snackbarStore'
import {useI18n} from 'vue-i18n'

const props = defineProps({
  eventType: { type: String, default: '' }
})

const { t } = useI18n()
const dialog = ref(false)
const excused = ref([])
const unexcused = ref([])
const snackbar = useSnackbarStore()
const loading = ref(false)


const route = useRoute()
const eventId = route.params.id

const excusedHeaders = [
  { title: t('event.absences.name'), value: 'name' },
  { title: t('event.absences.email'), value: 'email' },
  { title: t('event.absences.note'), value: 'note', minWidth: 200 },
]

const unexcusedHeaders = [
  { text: t('event.absences.name'), value: 'name' },
  { text: t('event.absences.email'), value: 'email' },
]


const fetchList = async () => {
  loading.value = true
  try {
    const { data } = await apiClient.get(
      `/event/${eventId}/absences/?mode=list`
    )
    excused.value = data.excused.map(item => ({
      ...item,
      name: `${item.first_name} ${item.last_name} (${item.index})`
    }))
    .sort((a, b) => a.last_name.localeCompare(b.last_name))
    unexcused.value = data.unexcused.map(item => ({
      ...item,
      name: `${item.first_name} ${item.last_name} (${item.index})`
    }))
    .sort((a, b) => a.last_name.localeCompare(b.last_name))
  } catch (e) {
    console.error('Chyba při načítání seznamu omluvenek', e)
  } finally {
    loading.value = false
  }
}


onMounted(() => {
  watch(dialog, open => {
    if (open) fetchList()
  })
})

// zkopíruje všechny emaily neomluvených do schránky
const copyUnexcusedEmails = () => {
  const emails = unexcused.value.map(u => u.email).join('; ')
  navigator.clipboard.writeText(emails)
    .then(() => {
      snackbar.success(t('event.absences.copy_success'))
    })
    .catch(err => {
      console.error('Chyba při kopírování emailů', err)
    })
}
</script>

<style scoped>
.subtitle-1 {
  margin-bottom: 0.5rem;
}

::v-deep() .v-table .v-table__wrapper table {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}


::v-deep() .v-table .v-table__wrapper > table > tbody > tr > td:not(:last-child), .v-table .v-table__wrapper > table > tbody > tr > th:not(:last-child) { 
  border-right: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding: 0 10px !important; 

}


.btn-ellipsis :deep(.v-btn__content){
  min-width: 0;             


}

</style>
